import { jwtDecode } from 'jwt-decode'

import {
    DEFAULT_MAIN_PAGE_DESKTOP_CATEGORY_SIZE,
    DEFAULT_MAIN_PAGE_MOBILE_CATEGORY_SIZE,
    DEFAULT_RECOMMENDATION_SIZE,
} from '@/constants/general'
import { AvailableLocale } from '@/i18n/helpers'
import {
    APICategoryDataType,
    APICategorySeoInfo,
    APICategoryShortInfo,
    APIGameInfoDTO,
    APIGameInfoType,
    APIGameRecommendation,
    APIGeneralInfo,
    APIRedirect,
    CategoryDataType,
    CategoryType,
    ClientType,
    FormSubmitData,
    GameRecommendationType,
    PlatformType,
    User,
} from '@/types'
import { GoogleSearchAPIResult } from '@/types/search'

import {
    ApiAcknowledgement,
    ApiResult,
    ApiResultWithRedirect,
    runApiAcknowledgement,
    runApiRequest,
} from './api-result'
import { FetchRequest, FetchResult, GamesAndCategoriesRequestParams } from './types'
import { isAPIRedirect } from './utils'

export interface IgpPurchase {
    id: string
    purchaseToken: string
    status: string
    externalId: string
    bridgeId?: string
    [key: string]: unknown
}

type PlaygamaConsentDetailsDTO = {
    authorization_id?: string
    redirect_uri?: string
    redirect_url?: string
    client?: {
        id?: string
        name?: string
    }
    scope?: string
    scopes?: string[]
}

export type PlaygamaConsentDetails = {
    appName: string
    authorizationId: string
    redirectTo?: string
    scopes: string[]
}

export type PlaygamaConsentLoadResult =
    | {
          status: 'consent-required'
          consentDetails: PlaygamaConsentDetails
      }
    | {
          status: 'resolved'
          redirectUrl: string
      }
    | {
          status: 'error'
          message: string
      }

type PlaygamaConsentDecisionDTO = {
    redirect_to?: string
    redirect_url?: string
}

export class ApiRequests {
    public request: FetchRequest
    public cmsRequest: FetchRequest

    constructor({ request, cmsRequest }: { request: FetchRequest; cmsRequest: FetchRequest }) {
        this.request = request
        this.cmsRequest = cmsRequest
    }

    public submitContactForm = (data: FormSubmitData): Promise<ApiAcknowledgement> => {
        const path = '/api/v1/email/report'
        return runApiAcknowledgement(path, () =>
            this.request(path, {
                method: 'POST',
                cache: 'no-store',
                body: JSON.stringify(data),
            }),
        )
    }

    public getGamesByCategories = async ({
        categoriesIds,
        platform,
        pagination,
        locale,
        withDebugInfo,
        pageType,
        pinnedGame,
        rankingClusterId,
        rankingAudience,
        includeUnsupportedPlatforms,
        scope,
    }: GamesAndCategoriesRequestParams): Promise<ApiResult<CategoryDataType>> => {
        const path = '/api/v1/categories/games'
        const search = new URLSearchParams()
        search.set('pagination', JSON.stringify(pagination))
        search.set('platform', platform)
        search.set('locale', locale)
        search.set('categoriesIds', JSON.stringify(categoriesIds))

        if (rankingClusterId) {
            search.set('cluster_id', rankingClusterId)
        }
        if (rankingAudience) {
            search.set('audnc', rankingAudience)
        }

        if (pinnedGame) {
            search.set('pinned_game', pinnedGame)
        }

        if (withDebugInfo) {
            search.set('debug', 'true')
        }

        if (includeUnsupportedPlatforms) {
            search.set('include_unsupported_platforms', 'true')
        }

        if (pageType) {
            search.set('type', pageType)
        }

        if (scope) {
            search.set('scope', scope)
        }

        const result = await runApiRequest(`${path}?${search}`, () =>
            this.request<APICategoryDataType>(`${path}?${search}`),
        )

        return result.status === 'ok'
            ? {
                  status: 'ok',
                  data: {
                      categories: result.data.categories.map((category) => ({
                          ...category,
                          games: category.games,
                      })),
                      pagination: result.data.pagination,
                  },
              }
            : result
    }

    public getMainPageGamesByCategories = async ({
        platform,
        locale,
        rankingClusterId,
        rankingAudience,
        withDebugInfo,
        scope,
    }: {
        platform: PlatformType
        locale: AvailableLocale
        rankingClusterId: string | undefined
        rankingAudience: string | undefined
        withDebugInfo: boolean
        scope?: string
    }): Promise<ApiResult<CategoryDataType>> => {
        const path = '/api/v1/categories/main-page'
        const search = new URLSearchParams()
        search.set('platform', platform)
        search.set(
            'pageSize',
            platform === 'desktop'
                ? DEFAULT_MAIN_PAGE_DESKTOP_CATEGORY_SIZE.toString()
                : DEFAULT_MAIN_PAGE_MOBILE_CATEGORY_SIZE.toString(),
        )
        search.set('locale', locale)

        if (rankingClusterId) {
            search.set('cluster_id', rankingClusterId)
        }
        if (rankingAudience) {
            search.set('audnc', rankingAudience)
        }

        if (withDebugInfo) {
            search.set('debug', 'true')
        }

        if (scope) {
            search.set('scope', scope)
        }

        const result = await runApiRequest(`${path}?${search}`, () =>
            this.request<APICategoryDataType>(`${path}?${search}`),
        )

        return result.status === 'ok'
            ? {
                  status: 'ok',
                  data: {
                      categories: result.data.categories.map((category) => ({
                          ...category,
                          games: category.games,
                      })),
                      pagination: result.data.pagination,
                  },
              }
            : result
    }

    public getMainCategories = (locale: AvailableLocale): Promise<ApiResult<APIGeneralInfo>> => {
        const path = '/api/v1/general/info'
        const search = new URLSearchParams()
        search.set('locale', locale)

        return runApiRequest(`${path}?${search}`, () => this.request<APIGeneralInfo>(`${path}?${search}`))
    }

    public getCurrentUser = async (): Promise<ApiResult<User | null>> => {
        if (typeof window === 'undefined') {
            return {
                status: 'error',
                message: 'Cannot get current user on SSR',
            }
        }

        const path = '/api/v1/user/current'
        const search = new URLSearchParams()
        search.set('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone)

        try {
            const { data, originalResponse } = await this.request<User>(`${path}?${search}`)
            return {
                status: 'ok',
                data: originalResponse.ok ? data || null : null,
            }
        } catch (error) {
            return {
                status: 'error',
                message: error instanceof Error ? error.message : String(error),
            }
        }
    }

    public getPlaygamaConsent = async (authorizationId: string): Promise<PlaygamaConsentLoadResult> => {
        const search = new URLSearchParams({
            authorization_id: authorizationId,
        })
        const path = `/api/v1/auth/playgama-oauth/consent?${search}`

        const result = await runApiRequest(path, () => this.request<PlaygamaConsentDetailsDTO>(path))

        if (result.status !== 'ok') {
            return {
                status: 'error',
                message: result.status === 'error' ? result.message : 'Unable to load Playgama authorization details',
            }
        }

        if (result.data.redirect_url) {
            return {
                status: 'resolved',
                redirectUrl: result.data.redirect_url,
            }
        }

        const appName = result.data.client?.name
        const scopes = result.data.scopes || result.data.scope?.split(/\s+/u).filter(Boolean)

        if (!appName || !result.data.authorization_id || !scopes?.length) {
            return {
                status: 'error',
                message: 'Unable to load Playgama authorization details',
            }
        }

        return {
            status: 'consent-required',
            consentDetails: {
                appName,
                authorizationId: result.data.authorization_id,
                redirectTo: result.data.redirect_uri,
                scopes,
            },
        }
    }

    public submitPlaygamaConsentDecision = async (
        authorizationId: string,
        decision: 'approve' | 'deny',
    ): Promise<{ status: 'ok'; redirectTo: string } | { status: 'error'; message: string }> => {
        const path = '/api/v1/auth/playgama-oauth/consent'
        const result = await runApiRequest(path, () =>
            this.request<PlaygamaConsentDecisionDTO>(path, {
                method: 'POST',
                body: JSON.stringify({
                    authorization_id: authorizationId,
                    decision,
                }),
            }),
        )

        const redirectTo = result.status === 'ok' ? result.data.redirect_to || result.data.redirect_url : undefined

        if (!redirectTo) {
            return {
                status: 'error',
                message:
                    result.status === 'error' ? result.message : 'Unable to submit Playgama authorization decision',
            }
        }

        return { status: 'ok', redirectTo }
    }

    public getGameByParam = async ({
        hru,
        locale,
        context,
        showHidden,
        clientType,
    }: {
        hru: string
        locale: AvailableLocale
        context: 'portal' | 'export'
        showHidden?: boolean
        clientType?: ClientType
    }): Promise<ApiResultWithRedirect<APIGameInfoType>> => {
        const path = `/api/v1/games/info/${hru}`
        const search = new URLSearchParams({
            locale,
            context,
        })

        if (showHidden) {
            search.append('show_hidden', 'true')
        }

        if (clientType) {
            search.append('client_type', clientType)
        }

        const result = await runApiRequest(`${path}?${search}`, () => this.request<APIGameInfoDTO>(`${path}?${search}`))

        if (result.status !== 'ok') {
            return result
        }

        if (isAPIRedirect(result.data)) {
            return {
                status: 'redirect',
                redirect: result.data,
            }
        }

        return {
            status: 'ok',
            data: result.data,
        }
    }

    public getRandomGameHRUs = (platform: PlatformType): Promise<ApiResult<string[]>> => {
        const path = '/api/v1/games/random-hrus'
        const search = new URLSearchParams()
        search.set('platform', platform)

        return runApiRequest(`${path}?${search}`, () => this.request<string[]>(`${path}?${search}`))
    }

    public getGamesCategoriesShortInfo = (
        type: CategoryType,
        locale: AvailableLocale,
    ): Promise<ApiResult<APICategoryShortInfo[]>> => {
        const path = `/api/v1/categories/short-info/${type}`
        const search = new URLSearchParams()

        search.set('locale', locale)

        return runApiRequest(`${path}?${search}`, () => this.request<APICategoryShortInfo[]>(`${path}?${search}`))
    }

    public getGamesCategoriesSeoById = async (
        id: string,
        locale: AvailableLocale,
        type: CategoryType,
    ): Promise<ApiResultWithRedirect<Exclude<APICategorySeoInfo, APIRedirect>>> => {
        const path = `/api/v1/categories/seo/${id}`
        const search = new URLSearchParams()
        search.set('locale', locale)
        search.set('type', type)

        const result = await runApiRequest(`${path}?${search}`, () =>
            this.request<APICategorySeoInfo>(`${path}?${search}`),
        )

        if (result.status !== 'ok') {
            return result
        }

        return isAPIRedirect(result.data)
            ? {
                  status: 'redirect',
                  redirect: result.data,
              }
            : {
                  status: 'ok',
                  data: result.data,
              }
    }

    // Decoder for signed jwt requests: decodes jwt body and attaches original data
    public signedRequest = async <T>(url: string, config: RequestInit = {}) => {
        const result = await this.request<string>(url, config)

        return result.data
            ? {
                  ...result,
                  data: {
                      jwt: result.data,
                      ...jwtDecode<T>(result.data),
                  },
              }
            : (result as FetchResult<T>)
    }

    public getGameRecommendations = <T extends GameRecommendationType[]>({
        hru,
        types,
        platform,
        locale,
        rankingClusterId,
        rankingAudience,
        limit = DEFAULT_RECOMMENDATION_SIZE,
    }: {
        hru: string
        types: T
        platform: PlatformType
        locale: AvailableLocale
        rankingClusterId: string | undefined
        rankingAudience: string | undefined
        limit?: number
    }): Promise<ApiResult<APIGameRecommendation<T>>> => {
        const search = new URLSearchParams()
        search.set('rec_types', types.join(','))
        search.set('limit', String(limit))
        search.set('platform', platform)
        search.set('locale', locale)

        if (rankingClusterId) {
            search.set('cluster_id', rankingClusterId)
        }

        if (rankingAudience) {
            search.set('audnc', rankingAudience)
        }

        const path = `/api/v1/games/recommendations/${hru}`
        return runApiRequest(`${path}?${search}`, () => this.request<APIGameRecommendation<T>>(`${path}?${search}`))
    }

    // these params are whitelisted on the portal-back
    public search = (params: {
        q: string
        num: string
        // interface lang
        hl: string
        // restrict results only on this lang
        lr: string
        // user geolocation
        // gl: ...
        // siteSearch: ...
        // siteSearchFilter: 'i' | 'e'
        commit?: string // indicator for portal-back to do extra logic
    }): Promise<ApiResult<GoogleSearchAPIResult>> => {
        const query = new URLSearchParams(params)
        return runApiRequest(`/api/v1/search/g?${query}`, () =>
            this.request<GoogleSearchAPIResult>(`/api/v1/search/g?${query}`),
        )
    }

    public confirmIgpDelivery = ({
        gameId,
        orderId,
        externalId,
    }: {
        gameId: string
        orderId: string
        externalId: string
    }): Promise<ApiAcknowledgement> => {
        const path = '/api/v1/in-game-payments/confirm-delivery'
        return runApiAcknowledgement(path, () =>
            this.request(path, {
                method: 'POST',
                body: JSON.stringify({ gameId, orderId, externalId }),
            }),
        )
    }

    public getIgpPurchases = (gameId: string): Promise<ApiResult<IgpPurchase[]>> => {
        const search = new URLSearchParams({ gameId })
        const path = `/api/v1/in-game-payments/purchases?${search}`
        return runApiRequest(path, () => this.request<IgpPurchase[]>(path))
    }

    public consumeIgpPurchase = ({
        gameId,
        orderId,
        externalId,
    }: {
        gameId: string
        orderId: string
        externalId: string
    }): Promise<ApiAcknowledgement> => {
        const path = '/api/v1/in-game-payments/consume'
        return runApiAcknowledgement(path, () =>
            this.request(path, {
                method: 'POST',
                body: JSON.stringify({ gameId, orderId, externalId }),
            }),
        )
    }

    public blockAccount = (): Promise<ApiAcknowledgement> => {
        const path = '/api/v1/user/block-account'
        return runApiAcknowledgement(path, () => this.request(path, { method: 'POST' }))
    }

    public logout = (): Promise<ApiAcknowledgement> => {
        const path = '/api/v1/auth/logout'
        return runApiAcknowledgement(path, () => this.request(path, { method: 'POST' }))
    }

    // Served by the portal express instance (src/server/cms/routes.ts), not portal-back.
    // Uses cmsRequest (no API_HOST prefix) so relative URLs work on the client and SSR
    // uses VUE_APP_HOST_URL as the host.
    public getGoogleProvidersEnabled = async (clid: string): Promise<boolean> => {
        try {
            const search = new URLSearchParams({ clid })
            const { data } = await this.cmsRequest<{ enabled: boolean }>(`/ads/google-providers-enabled?${search}`)

            return !!data?.enabled
        } catch {
            return false
        }
    }
}
